import React from 'react';
import ReadinessGauge from '../components/ReadinessGauge';
import DistributionChart from '../components/DistributionChart';


const StoreView = ({ data }) => {
  if (!data) return <div className="loading">Loading Store view...</div>;

  const { storeStats, wowComparison, incompleteEmployees, allComplete, isFirstUpload } = data;

  const getTrendIcon = (trend) => {
    if (trend === 'up') return '↑';
    if (trend === 'down') return '↓';
    return '→';
  };

  const getTrendClass = (trend) => {
    if (trend === 'up') return 'trend-up';
    if (trend === 'down') return 'trend-down';
    return 'trend-same';
  };

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return 'status-ready';
    if (percentage >= 60) return 'status-caution';
    return 'status-critical';
  };

  // Determine action items
  const getActionItems = () => {
    const items = [];
    const completion = parseFloat(storeStats.completion_percentage);

    if (allComplete) {
      items.push({ type: 'success', text: 'All employees have completed training! Congratulations!' });
      items.push({ type: 'success', text: 'Maintain this level of excellence' });
    } else {
      if (isFirstUpload) {
        if (completion >= 80) {
          items.push({ type: 'success', text: 'Store is compliant - push to 100%' });
          items.push({ type: 'info', text: `${incompleteEmployees.length} employees remaining` });
        } else if (completion >= 60) {
          items.push({ type: 'warning', text: 'Schedule time for employees to complete training' });
          items.push({ type: 'info', text: `${incompleteEmployees.length} employees need to complete training` });
        } else {
          items.push({ type: 'critical', text: 'Immediate action required to achieve compliance' });
          items.push({ type: 'critical', text: `${incompleteEmployees.length} employees are incomplete` });
          items.push({ type: 'info', text: 'Ensure all team members have access and scheduled time' });
        }
      } else {
        if (wowComparison.trend === 'up') {
          items.push({ type: 'success', text: `Great progress! Up ${wowComparison.change.toFixed(1)}% from last week` });
        } else if (wowComparison.trend === 'down') {
          items.push({ type: 'warning', text: `Completion declined by ${Math.abs(wowComparison.change).toFixed(1)}%` });
        }

        if (completion < 80) {
          items.push({ type: 'warning', text: 'Follow up with incomplete employees' });
        }
      }
    }

    return items;
  };

  const actionItems = getActionItems();

  return (
    <div className="store-view">
      <div className="store-header">
        <h2>{storeStats.training_location_name}</h2>
        <p className="store-code">PC: {storeStats.profit_center_code || storeStats.location_pc}</p>
        <p className="store-address">
          {storeStats.address}, {storeStats.city}, {storeStats.state} {storeStats.zip}
        </p>
        <div className="store-hierarchy">
          <span>VP: {storeStats.vp}</span>
          <span>Director: {storeStats.director}</span>
          <span>MUM: {storeStats.mum}</span>
          <span>Manager: {storeStats.store_manager}</span>
        </div>
      </div>

      <div className="completion-box-large">
        <div className={`completion-main ${getCompletionColor(storeStats.completion_percentage)}`}>
          <h3>Store Completion</h3>
          <div className="percentage-large">
            {storeStats.completion_percentage}%
          </div>
          {wowComparison && (
            <div className={`trend-indicator ${getTrendClass(wowComparison.trend)}`}>
              {getTrendIcon(wowComparison.trend)} {Math.abs(wowComparison.change).toFixed(1)}%
              <span className="trend-label">vs. last week</span>
            </div>
          )}
        </div>

        <div className="employee-stats">
          <div className="stat-row">
            <span className="label">Total Employees:</span>
            <span className="value">{storeStats.employee_count}</span>
          </div>
          <div className="stat-row ready">
            <span className="label">Completed:</span>
            <span className="value">{storeStats.employees_completed}</span>
          </div>
          <div className="stat-row caution">
            <span className="label">In Progress:</span>
            <span className="value">{storeStats.employees_in_progress}</span>
          </div>
          <div className="stat-row critical">
            <span className="label">Not Started:</span>
            <span className="value">{storeStats.employees_not_started}</span>
          </div>
        </div>
      </div>

      <div className="charts-grid">
        <ReadinessGauge
          percentage={parseFloat(storeStats.completion_percentage)}
          title="Location Readiness"
        />
        <DistributionChart
          ready={storeStats.employees_completed}
          caution={storeStats.employees_in_progress}
          critical={storeStats.employees_not_started}
          title="Employee Status Distribution"
        />
      </div>

      <div className={`compliance-status ${storeStats.status_color}`}>
        <h3>Compliance Status</h3>
        <div className="status-indicator">
          <span className={`badge badge-${storeStats.status_color}`}>
            {storeStats.compliance_status}
          </span>
        </div>
      </div>

      <div className="action-items">
        <h3>Action Items</h3>
        <ul>
          {actionItems.map((item, index) => (
            <li key={index} className={`action-item-${item.type}`}>
              {item.text}
            </li>
          ))}
        </ul>
      </div>

      {allComplete ? (
        <div className="success-message">
          <h3>🎉 Congratulations!</h3>
          <p>All employees at this location have completed their training.</p>
        </div>
      ) : (
        <div className="data-table">
          <h3>Incomplete Employees ({incompleteEmployees.length})</h3>
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Employee Name</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {incompleteEmployees.map((employee, index) => (
                  <tr key={index}>
                    <td>{employee.learner_name}</td>
                    <td>
                      <span className={`status-badge status-${employee.status === 'In Progress' ? 'yellow' : 'red'}`}>
                        {employee.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreView;
